import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ExternalLink, Loader2, UploadCloud, ArrowRight } from "lucide-react";
import { tarifasApi, errMsg, fmtMoney, tierLabel } from "./api";

export const ApplyDialog = ({ fileId, proposalIds, open, onClose, onConfirm, applying }) => {
  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setPlan(null);
    setErr("");
    tarifasApi.applyPreview(fileId, proposalIds)
      .then((d) => alive && setPlan(d))
      .catch((e) => alive && setErr(errMsg(e, "No se pudo preparar la simulación")));
    return () => { alive = false; };
  }, [open, fileId, proposalIds]);

  const totalChanges = plan?.items.reduce((n, it) => n + it.changes, 0) || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !applying && onClose()}>
      <DialogContent className="rounded-md max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="apply-dialog">
        <DialogHeader>
          <DialogTitle>Así quedará en KimKim (simulación, aún no se ha cambiado nada)</DialogTitle>
          <DialogDescription>
            {plan ? `${plan.items.length} actividad(es) de ${plan.provider} · ${totalChanges} importes cambian · marca «${plan.year} // ${plan.alias}» en las notas de precio. ${plan.note}` : "Preparando la simulación…"}
          </DialogDescription>
        </DialogHeader>
        {err && <p className="text-sm text-red-700" data-testid="apply-dialog-error">{err}</p>}
        {!plan && !err && <p className="text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Calculando…</p>}
        {plan && (
          <div className="space-y-4">
            {plan.items.map((it) => (
              <div key={it.proposal_id} className="rounded-md border p-3" data-testid={`apply-item-${it.proposal_id}`}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold">{it.provider_name}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground" />
                  <span className="font-semibold">{it.kimkim_title}</span>
                  <a href={it.kimkim_url} target="_blank" rel="noreferrer" className="text-primary underline text-xs flex items-center gap-0.5">abrir en KimKim <ExternalLink className="h-3 w-3" /></a>
                  <Badge className="rounded-md bg-slate-100 text-slate-700 hover:bg-slate-100">{it.kimkim_pricing_type === "per_person_price" ? "por persona" : "por grupo"} · {it.currency || "EUR"}</Badge>
                  {it.kimkim_owner === "kimkim" && <Badge className="rounded-md bg-red-100 text-red-800 hover:bg-red-100" data-testid={`apply-owner-${it.proposal_id}`}>propiedad de Kimkim · no se actualiza</Badge>}
                  {!it.applicable && it.kimkim_owner !== "kimkim" && <Badge className="rounded-md bg-amber-100 text-amber-800 hover:bg-amber-100">no se aplicará (estado {it.status})</Badge>}
                </div>
                <table className="mt-2 w-full text-xs">
                  <thead className="text-muted-foreground"><tr><th className="text-left font-medium py-1">Tramo</th><th className="text-right font-medium">Ahora</th><th className="text-right font-medium">Quedará</th><th className="text-left font-medium pl-3">Neto</th></tr></thead>
                  <tbody>
                    {it.rows.map((r) => (
                      <tr key={`${r.min}-${r.max}`} className={`border-t ${r.changed ? "font-semibold" : ""}`}>
                        <td className="py-1">{tierLabel(r.min, r.max)}</td>
                        <td className="text-right font-mono">{fmtMoney(r.current, "")}</td>
                        <td className={`text-right font-mono ${r.changed ? "text-emerald-700" : ""}`}>{fmtMoney(r.computed, "")}</td>
                        <td className="pl-3 text-muted-foreground font-normal">{fmtMoney(r.net, "")} × 1,08 ↑ euro{r.changed ? "" : " · igual"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 grid gap-1 md:grid-cols-2 text-xs">
                  <div className="rounded-md bg-slate-50 p-2"><p className="text-muted-foreground">Notas de precio ahora</p><p className="font-mono whitespace-pre-wrap" data-testid={`apply-notes-before-${it.proposal_id}`}>{it.notes_before || "(vacías)"}</p></div>
                  <div className="rounded-md bg-emerald-50 p-2"><p className="text-muted-foreground">Notas de precio después</p><p className="font-mono whitespace-pre-wrap" data-testid={`apply-notes-after-${it.proposal_id}`}>{it.notes_after}</p></div>
                </div>
                {it.supplements?.length > 0 && (
                  <p className="mt-1 text-xs text-sky-800" data-testid={`apply-supplements-${it.proposal_id}`}>
                    Suplementos del tarifario que se añaden a las notas (no se suman al precio): {it.supplements.join(" · ")}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" className="rounded-md" onClick={onClose} disabled={applying} data-testid="apply-dialog-cancel">Cancelar</Button>
          <Button className="rounded-md" onClick={onConfirm} disabled={!plan || applying || !plan.items.some((i) => i.applicable)} data-testid="apply-dialog-confirm">
            {applying ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <UploadCloud className="h-3 w-3 mr-1" />} Sí, actualizar en KimKim
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
